import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';

// API 응답 인터셉터
export const handleApiError = async (error: any, originalRequest?: () => Promise<any>) => {
  const authStore = useAuthStore.getState();
  
  // 401 Unauthorized 에러 처리
  if (error?.response?.status === 401 || error?.status === 401) {
    console.log('🚫 401 에러 감지 - 인증 만료');
    
    // 토큰 갱신 시도
    const refreshed = await authStore.refreshAuthToken();
    
    if (refreshed && originalRequest) {
      console.log('🔄 토큰 갱신 후 요청 재시도');
      try {
        return await originalRequest();
      } catch (retryError) {
        console.error('재시도 실패:', retryError);
      }
    }
    
    // 토큰 갱신 실패 시 로그아웃
    console.log('🚪 자동 로그아웃 처리');
    authStore.clearAuth();
    
    // 토스트 알림으로 로그아웃 알림
    toast.error('로그인이 만료되었습니다. 다시 로그인해주세요.', {
      duration: 4000,
      style: {
        background: '#fef2f2',
        color: '#dc2626',
        border: '1px solid #fecaca',
        borderRadius: '12px',
        padding: '12px 16px',
        fontSize: '14px',
        fontWeight: '500',
      },
    });
    
    // 로그인 페이지로 리디렉션 (현재 페이지 새로고침으로 AuthModal 트리거)
    // 약간의 지연을 두어 토스트가 표시되도록 함
    setTimeout(() => {
      window.location.reload();
    }, 1000);
    
    return Promise.reject(new Error('Authentication failed'));
  }
  
  // 네트워크 에러 또는 서버 에러
  if (error?.code === 'NETWORK_ERROR' || error?.response?.status >= 500) {
    console.log('🌐 네트워크/서버 에러:', error.message);
    
    // 사용자 친화적 에러 메시지
    let errorMessage = '서버에 일시적인 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    
    if (error?.code === 'NETWORK_ERROR') {
      errorMessage = '네트워크 연결을 확인해주세요.';
    } else if (error?.response?.status === 500) {
      errorMessage = '서버 내부 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
    } else if (error?.response?.status === 502) {
      errorMessage = '서버가 일시적으로 사용할 수 없습니다. 잠시 후 다시 시도해주세요.';
    } else if (error?.response?.status === 503) {
      errorMessage = '서버가 점검 중입니다. 잠시 후 다시 시도해주세요.';
    }
    
    // 토스트 알림으로 에러 메시지 표시
    toast.error(errorMessage, {
      duration: 5000,
      style: {
        background: '#fef2f2',
        color: '#dc2626',
        border: '1px solid #fecaca',
        borderRadius: '12px',
        padding: '12px 16px',
        fontSize: '14px',
        fontWeight: '500',
      },
    });
    
    // 여기서는 로그아웃하지 않고 사용자에게 알림만 표시
    return Promise.reject(error);
  }
  
  return Promise.reject(error);
};

// Fetch API 래퍼 (자동 토큰 갱신 포함)
export const apiRequest = async (url: string, options: RequestInit = {}) => {
  const authStore = useAuthStore.getState();
  
  // 로그인 만료 확인
  if (authStore.isAuthenticated && !authStore.checkAuthExpiry()) {
    throw new Error('Login expired');
  }
  
  // Authorization 헤더 추가
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  if (authStore.accessToken) {
    headers['Authorization'] = `Bearer ${authStore.accessToken}`;
  }
  
  const requestOptions: RequestInit = {
    ...options,
    headers,
  };
  
  try {
    const response = await fetch(url, requestOptions);
    
    if (!response.ok) {
      const error = {
        status: response.status,
        statusText: response.statusText,
        response: response,
      };
      
      return handleApiError(error, () => fetch(url, requestOptions));
    }
    
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

// 자동 로그인 초기화 함수
export const initializeAutoLogin = () => {
  const authStore = useAuthStore.getState();
  
  // 저장된 로그인 정보 확인
  const savedExpiry = localStorage.getItem('loginExpiry');
  const savedToken = localStorage.getItem('accessToken');
  
  if (savedExpiry && savedToken) {
    const expiryTime = parseInt(savedExpiry);
    const now = Date.now();
    
    if (now < expiryTime) {
      console.log('🔐 자동 로그인 유효:', new Date(expiryTime).toLocaleString());
      // 이미 zustand persist가 복원했으므로 추가 작업 불필요
      return true;
    } else {
      console.log('⏰ 자동 로그인 만료');
      authStore.clearAuth();
      return false;
    }
  }
  
  return false;
};
